import { useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import { useUpdateControlProgramRightsForUserMutation } from "org/user/queries/useUpdateControlProgramRightsForUserMutation";

import { ControlProgramRow } from "./useUserControlProgramListTable";

interface UseRemoveControlProgramAccessRightUtilsProps {
    userId: number;
    selectedRows: Row<ControlProgramRow>[];
    deselectAll: () => void;
}

export function useRemoveControlProgramAccessRightUtils({
    userId,
    selectedRows,
    deselectAll,
}: UseRemoveControlProgramAccessRightUtilsProps) {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { mutateAsync, isPending } =
        useUpdateControlProgramRightsForUserMutation();

    // A program must keep at least one admin
    const removeAccessRightToolState: TextToolState = ((): TextToolState => {
        if (selectedRows.length === 0) {
            return { type: "DISABLED" };
        }
        if (selectedRows.some((row) => row.original.isSoleAdmin)) {
            return {
                type: "DISABLED",
                reason: t("UserManagement_SoleAdmin_DisabledTooltip"),
            };
        }
        return { type: "ENABLED" };
    })();

    const removeAccessRight = async () => {
        const controlProgramIds = selectedRows.map((row) => row.original.id);
        try {
            await mutateAsync({
                userId,
                data: { newUserId: null, controlProgramIds },
            });
            deselectAll();
            setIsModalOpen(false);
        } catch (error) {
            console.error("Failed to remove access right:", error);
        }
    };

    return {
        removeAccessRightToolState,
        isRemoveAccessRightModalOpen: isModalOpen,
        openRemoveAccessRightModal: () => setIsModalOpen(true),
        closeRemoveAccessRightModal: () => setIsModalOpen(false),
        removeAccessRight,
        isRemovingAccessRight: isPending,
    };
}
